import React, { useEffect, useState, useCallback } from "react";
import { entityService } from "../services/api";
import { Users, Search, Plus, ChevronLeft, ChevronRight, User, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const ENTITY_TYPES = ["person", "company", "auditor", "legal_group"];

export default function EntitiesPage() {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newEntity, setNewEntity] = useState({
    name: "",
    entity_type: "person",
    address: "",
    identification_no: ""
  });

  const fetchEntities = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (typeFilter) params.entity_type = typeFilter;
      
      const response = await entityService.getAll(params);
      setEntities(response.data.items || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      toast.error("Failed to fetch entities");
    } finally {
      setLoading(false);
    }
  }, [page, search, typeFilter]);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter]);

  const handleCreateEntity = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await entityService.create(newEntity);
      toast.success("Entity created successfully");
      setShowCreateModal(false);
      setNewEntity({
        name: "",
        entity_type: "person",
        address: "",
        identification_no: ""
      });
      fetchEntities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create entity");
    } finally {
      setCreating(false);
    }
  };

  const getEntityIcon = (type) => {
    switch (type) {
      case 'person':
        return <User className="w-4 h-4" />;
      case 'company':
        return <Building2 className="w-4 h-4" />;
      default:
        return <Users className="w-4 h-4" />;
    }
  };

  return (
    <div className="p-6 sm:p-8" data-testid="entities-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Entities</h1>
          <p className="text-sm text-slate-500 mt-1">Manage persons and organizations</p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2"
          data-testid="create-entity-btn"
        >
          <Plus className="w-4 h-4" />
          New Entity
        </Button>
      </div>

      {/* Filters */}
      <div className="enterprise-card p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search entities..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 form-input"
                data-testid="entity-search-input"
              />
            </div>
          </div>
          <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[160px]" data-testid="type-filter">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {ENTITY_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Entities Table */}
      <div className="enterprise-card">
        {loading ? (
          <div className="p-8">
            <div className="animate-pulse space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-slate-100 rounded"></div>
              ))}
            </div>
          </div>
        ) : entities.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No entities found</p>
            <p className="text-sm text-slate-400 mt-1">Try adjusting your search or filters</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th className="hidden md:table-cell">ID Number</th>
                    <th className="hidden lg:table-cell">Address</th>
                  </tr>
                </thead>
                <tbody>
                  {entities.map((entity) => (
                    <tr key={entity.id} data-testid={`entity-row-${entity.id}`}>
                      <td>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-400">{getEntityIcon(entity.entity_type)}</span>
                          <span className="font-medium text-slate-900">{entity.name}</span>
                        </div>
                      </td>
                      <td>
                        <span className="badge bg-slate-100 text-slate-700 border-slate-200">
                          {entity.entity_type}
                        </span>
                      </td>
                      <td className="hidden md:table-cell">{entity.identification_no || '-'}</td>
                      <td className="hidden lg:table-cell max-w-xs truncate">{entity.address || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Create Entity Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Entity</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateEntity}>
            <div className="space-y-4 py-4">
              <div>
                <Label className="form-label">Name *</Label>
                <Input
                  value={newEntity.name}
                  onChange={(e) => setNewEntity({...newEntity, name: e.target.value})}
                  required
                  placeholder="Full name"
                  className="form-input"
                  data-testid="new-entity-name"
                />
              </div>
              <div>
                <Label className="form-label">Type *</Label>
                <Select 
                  value={newEntity.entity_type} 
                  onValueChange={(v) => setNewEntity({...newEntity, entity_type: v})}
                >
                  <SelectTrigger data-testid="new-entity-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Identification Number</Label>
                <Input
                  value={newEntity.identification_no}
                  onChange={(e) => setNewEntity({...newEntity, identification_no: e.target.value})}
                  placeholder="ID / Passport / Registration number"
                  className="form-input"
                />
              </div>
              <div>
                <Label className="form-label">Address</Label>
                <Input
                  value={newEntity.address}
                  onChange={(e) => setNewEntity({...newEntity, address: e.target.value})}
                  placeholder="Full address"
                  className="form-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" disabled={creating} data-testid="submit-new-entity">
                {creating ? "Creating..." : "Create Entity"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
